import { Component, computed, inject, signal } from '@angular/core';
import { PermissionService } from '../../permissions/permission.service';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { CountriesService, CountryDto } from '../../../shared/services/countries.service';
import {
  AdminButtonComponent,
  AdminEmptyStateComponent,
  AdminModalComponent,
  AdminPageHeaderComponent,
  AdminTableComponent,
  StatusBadgeComponent,
} from '../../../shared/admin';
import { countryToDuplicatePayload, slugFromName } from './country-form.utils';

@Component({
  selector: 'app-countries-page',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    AdminPageHeaderComponent,
    AdminButtonComponent,
    AdminTableComponent,
    AdminEmptyStateComponent,
    AdminModalComponent,
    StatusBadgeComponent,
  ],
  templateUrl: './countries-page.component.html',
})
export class CountriesPageComponent {
  private readonly service = inject(CountriesService);
  private readonly router = inject(Router);
  private readonly permissions = inject(PermissionService);

  readonly showCreate = computed(() => this.permissions.canShow('website.countries', 'create'));
  readonly canCreate = computed(() => this.permissions.canExecute('website.countries', 'create'));
  readonly showEdit = computed(() => this.permissions.canShow('website.countries', 'edit'));
  readonly canEdit = computed(() => this.permissions.canExecute('website.countries', 'edit'));
  readonly showDelete = computed(() => this.permissions.canShow('website.countries', 'delete'));
  readonly canDelete = computed(() => this.permissions.canExecute('website.countries', 'delete'));
  readonly showDuplicate = computed(() => this.permissions.canShow('website.countries', 'duplicate'));
  readonly canDuplicate = computed(() => this.permissions.canExecute('website.countries', 'duplicate'));
  readonly showPublish = computed(() => this.permissions.canShow('website.countries', 'publish'));
  readonly canPublish = computed(() => this.permissions.canExecute('website.countries', 'publish'));

  readonly countries = signal<CountryDto[]>([]);
  readonly duplicateModalOpen = signal(false);
  readonly duplicateLoading = signal(false);
  readonly duplicateSaving = signal(false);
  readonly duplicateError = signal<string | null>(null);
  readonly duplicateSource = signal<CountryDto | null>(null);

  duplicateName = '';
  readonly duplicateSlugPreview = () => slugFromName(this.duplicateName);

  constructor() {
    this.refresh();
  }

  refresh(): void {
    this.service.listPlatformCountries().subscribe((items) => this.countries.set(items));
  }

  goNewCountry(): void {
    if (!this.canCreate()) return;
    void this.router.navigate(['/platform/countries/new']);
  }

  goEditCountry(id: string): void {
    if (!this.canEdit()) return;
    void this.router.navigate(['/platform/countries', id, 'edit']);
  }

  togglePublish(country: CountryDto): void {
    if (!this.canPublish()) return;
    const next = country.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
    this.service.update(country._id, { status: next }).subscribe(() => this.refresh());
  }

  remove(country: CountryDto): void {
    if (!this.canDelete()) return;
    if (!confirm(`Delete ${country.name}?`)) return;
    this.service.remove(country._id).subscribe(() => this.refresh());
  }

  openDuplicate(country: CountryDto): void {
    if (!this.canDuplicate()) return;
    this.duplicateError.set(null);
    this.duplicateName = `${country.name} Copy`;
    this.duplicateSource.set(null);
    this.duplicateModalOpen.set(true);
    this.duplicateLoading.set(true);
    this.service.getPlatformCountry(country._id).subscribe({
      next: (full) => {
        this.duplicateSource.set(full);
        this.duplicateLoading.set(false);
      },
      error: () => {
        this.duplicateLoading.set(false);
        this.duplicateError.set('Could not load country to duplicate.');
      },
    });
  }

  closeDuplicate(): void {
    if (this.duplicateSaving()) return;
    this.duplicateModalOpen.set(false);
    this.duplicateSource.set(null);
    this.duplicateError.set(null);
  }

  confirmDuplicate(): void {
    const source = this.duplicateSource();
    const name = this.duplicateName.trim();
    const slug = slugFromName(name);
    if (!source) {
      this.duplicateError.set('Country data is still loading.');
      return;
    }
    if (!name) {
      this.duplicateError.set('Enter a name for the new country.');
      return;
    }
    if (!slug) {
      this.duplicateError.set('Name must include at least one letter or number for the slug.');
      return;
    }

    this.duplicateSaving.set(true);
    this.duplicateError.set(null);
    const payload = countryToDuplicatePayload(source, name, slug);
    this.service.create(payload).subscribe({
      next: (created) => {
        this.duplicateSaving.set(false);
        this.duplicateModalOpen.set(false);
        void this.router.navigate(['/platform/countries', created._id, 'edit']);
      },
      error: (err: unknown) => {
        this.duplicateSaving.set(false);
        const msg =
          err && typeof err === 'object' && 'error' in err
            ? String((err as { error?: { message?: string } }).error?.message ?? '')
            : '';
        this.duplicateError.set(
          msg.includes('slug') ? 'A country with this slug already exists. Try a different name.' : msg || 'Could not duplicate country.',
        );
      },
    });
  }
}
